// "Show only the selected elements" - turning the tree's TICKS into the
// element ids the viewer isolates (owner, 2026-09-20).
//
// The ticks are node keys (scopeKeys.js), never rows read off the rc-tree:
// a collapsed branch's children are placeholders, so a tick on a category
// must still mean every element under it. Each key is resolved BY THE
// SERVER (root-f5's tree-uids), which counts first and refuses a row over
// its limit with a count - the toggle then says so and isolates nothing,
// because half an isolation reads as a filtered model and is not one.
import { resolveNodeUids, resolveRecordUids } from "api/tracking/trackingElementsApi";

// The resolver's own ceiling (root-f5's treeUids DEFAULT_LIMIT). A row over
// it is refused server-side; the TOTAL is refused here, because twelve rows
// of 140,000 each pass one by one and then kill the tab (root-f5,
// 2026-09-20).
const LIMIT = 150000;
// How many rows to resolve at once: the endpoint shares a scan between
// identical concurrent calls and caches 10 min, so parallel is cheap there.
const LANES = 4;

export const parseKey = (key) => {
  const raw = String(key || "");
  const at = raw.indexOf(":");
  if (at < 0) return null;
  const kind = raw.slice(0, at);
  const rest = raw.slice(at + 1);
  const parts = rest.split("|");
  if (kind === "element") return { kind, uniqueId: rest };
  // Revit names are free text and DO contain "|", so everything after the
  // field's position belongs to it: a type called "1in x 2in | long" used
  // to arrive truncated and isolate the wrong set (root-f5, 2026-09-20).
  // Category names are fixed vocabulary and carry no pipe; a family
  // containing one is still ambiguous in a type key, and the backend's
  // matchForNodeKeys reads it the same way, so both ends agree.
  if (kind === "category") return { kind, category: rest };
  if (kind === "family") return { kind, category: parts[0] || "", family: parts.slice(1).join("|") };
  if (kind === "type") return { kind, category: parts[0] || "", family: parts[1] || "", typeName: parts.slice(2).join("|") };
  // The DETAILED TREE's rows (recordKeys.js): the record's own category and
  // group. They live here rather than in a second resolver so that a
  // selection made across both trees is folded, counted and refused by ONE
  // set of rules - only the question put to the server differs.
  if (kind === "rc") return { kind: "record", category: rest, group: null };
  if (kind === "rg") return { kind: "record", category: parts[0] || "", group: parts.slice(1).join("|") };
  // A SIZE NODE of Conduits (recordKeys.js, back/utils/conduitSizes).
  if (kind === "rs") {
    const size = parts[2] === "unknown" ? "unknown" : Number(parts[2]);
    if (parts.length !== 3 || !parts[0] || (size !== "unknown" && !(size > 0))) return null;
    return { kind: "record", category: parts[0], group: null, material: parts[1], size };
  }
  return null;
};

// AN ANSWER MUST BE A SHAPE WE ASKED FOR. A response that is none of the
// three - a refusal, a hash rule, or a compact id list - used to fold in
// as "no ids", which is indistinguishable from an empty row: the rule
// came back missing what it should have carried, the page set null, and
// nobody was told. A shape we do not recognise is now an ERROR, which the
// caller records and shows, because "sometimes nothing is selected" is
// the most expensive bug shape there is (leader, 2026-09-20).
function readAnswer(r, p) {
  if (!r || typeof r !== 'object') throw new Error(`The server gave no answer for ${p.category || 'that row'}`);
  if (r.tooMany) return r;
  if (r.kind === 'hashes') {
    if (!Array.isArray(r.hashes) || !Array.isArray(r.singles)) {
      throw new Error(`A hash answer for ${p.category || 'that row'} carried no hashes`);
    }
    return r;
  }
  // The id shape: compact prefixes and/or a plain list. `count` alone is
  // not enough - an answer that says 13 and carries nothing is the bug.
  const hasIds = (r.byPrefix && typeof r.byPrefix === 'object') || Array.isArray(r.other);
  if (!hasIds) throw new Error(`The answer for ${p.category || 'that row'} was neither ids nor a rule`);
  return r;
}

// One row, asked of the tree it belongs to.
const askRow = (projectId, p, { prefixes, mode, resolve, resolveRecord }) => (
  p.kind === "record"
    ? resolveRecord(projectId, { category: p.category, group: p.group, ...(p.size !== undefined ? { material: p.material, size: p.size } : {}), documentIds: prefixes, ...(mode ? { mode } : {}) })
    : resolve(projectId, { category: p.category, family: p.family || "", typeName: p.typeName || "", prefixes, ...(mode ? { mode } : {}) })
);

/**
 * keys -> { uids, tooMany } - tooMany is { key, count, limit } for the
 * FIRST row that is too big, and then uids is empty: the caller refuses
 * as a whole rather than isolating the rest.
 * `resolve` is injectable for tests.
 */
/**
 * The selection as the VIEWER can test it (root-f5's rule isolate, on prod
 * 2026-09-20). A row comes back either as ids or as fragment hashes -
 * whichever is smaller for that row - and both fold into ONE rule:
 *
 *   { hashes, singles, prefixes, elementCount }
 *
 * `singles` carries both kinds of per-element truth: the merged-geometry
 * uids a hash row cannot cover, and every id from a row answered as ids.
 * `prefixes` must be sent whenever a document filter is on: a geometry pool
 * is shared across documents, so without them a hash would show copies from
 * files the person has switched off.
 *
 * -> { rule, elementCount, anyHashes, tooMany }
 */
/**
 * `exclude` is what was dropped out of the ticked branch - "this category
 * except that one" (owner, 2026-09-20). It rides to the viewer as
 * excludeUids and NOTHING ELSE, because a hash is SHARED geometry: an
 * exclusion expressed as a hash would remove every twin of it across the
 * model. So the dropped rows are resolved to ids here, the matcher tests
 * them first, and the honest ceiling is the length of that id list - not
 * the size of the selection.
 */
export async function ruleForSelection(projectId, keys, { prefixes = "", exclude = [], resolve = resolveNodeUids, resolveRecord = resolveRecordUids, limit = LIMIT, lanes = LANES } = {}) {
  const parsed = (keys || []).map(parseKey).filter(Boolean);
  // Ticks may be restored from a saved selection: one this page no longer
  // reads is SKIPPED AND NAMED in the answer, not dropped in silence.
  const skipped = (keys || []).filter((k) => !parseKey(k)).map((k) => ({ key: String(k), reason: `"${String(k).slice(0, 80)}" is not a kind of tree row this page knows` }));
  const hashes = new Set();
  const singles = new Set(parsed.filter((p) => p.kind === "element").map((p) => p.uniqueId));
  const rows = parsed.filter((p) => p.kind !== "element");
  let elementCount = singles.size;
  let anyHashes = false;

  const answers = [];
  for (let i = 0; i < rows.length; i += lanes) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await Promise.all(rows.slice(i, i + lanes).map((p) => askRow(projectId, p, { prefixes, mode: "auto", resolve, resolveRecord })));
    answers.push(...batch);
  }
  for (let i = 0; i < rows.length; i += 1) {
    const p = rows[i];
    const r = readAnswer(answers[i], p);
    if (r.tooMany) return { rule: null, elementCount: 0, anyHashes: false, tooMany: { key: p, count: r.count, limit: r.limit } };
    elementCount += Number(r.count) || 0;
    if (r.kind === "hashes") {
      anyHashes = true;
      (r.hashes || []).forEach((h) => hashes.add(h));
      (r.singles || []).forEach((u) => singles.add(u));
    } else {
      const byPrefix = r.byPrefix || {};
      for (const prefix of Object.keys(byPrefix)) {
        for (const id of String(byPrefix[prefix]).split(",")) if (id) singles.add(`${prefix}-${id}`);
      }
      (r.other || []).forEach((u) => singles.add(u));
    }
    // The per-element half still has a ceiling: a mixed selection of many
    // id-answered rows can still be more than a browser should hold, even
    // though a category no longer can be (root-f5, 2026-09-20).
    if (singles.size > limit) {
      return { rule: null, elementCount: 0, anyHashes: false, tooMany: { key: p, count: singles.size, limit, total: true } };
    }
  }
  // What was dropped, as ids. Element keys are ids already; a dropped row
  // is resolved like any other row, and a dropped row too big to answer as
  // ids simply does not reach the viewer - the count still subtracts it,
  // and the isolate would be wrong, so that case refuses rather than
  // showing what the person just dropped.
  const excludeUids = new Set();
  let excludedCount = 0;
  const exParsed = (exclude || []).map(parseKey).filter(Boolean);
  const exRows = exParsed.filter((p) => p.kind !== "element");
  exParsed.filter((p) => p.kind === "element").forEach((p) => { excludeUids.add(p.uniqueId); excludedCount += 1; });
  for (let i = 0; i < exRows.length; i += lanes) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await Promise.all(exRows.slice(i, i + lanes).map((p) => askRow(projectId, p, { prefixes, mode: "", resolve, resolveRecord })));
    for (let j = 0; j < batch.length; j += 1) {
      const r = batch[j] || {};
      if (r.tooMany) return { rule: null, elementCount: 0, anyHashes: false, tooMany: { key: exRows[i + j], count: r.count, limit: r.limit, excluded: true } };
      excludedCount += Number(r.count) || 0;
      const byPrefix = r.byPrefix || {};
      for (const prefix of Object.keys(byPrefix)) {
        for (const id of String(byPrefix[prefix]).split(",")) if (id) excludeUids.add(`${prefix}-${id}`);
      }
      (r.other || []).forEach((u) => excludeUids.add(u));
    }
  }
  const net = Math.max(0, elementCount - excludedCount);

  return {
    rule: {
      hashes: [...hashes],
      singles: [...singles],
      prefixes: String(prefixes || "").split(",").map((x) => x.trim()).filter(Boolean),
      excludeUids: [...excludeUids],
      elementCount: net,
    },
    elementCount: net,
    excludedCount,
    anyHashes,
    tooMany: null,
    ...(skipped.length ? { skipped } : {}),
  };
}

export async function uidsForKeys(projectId, keys, { prefixes = "", resolve = resolveNodeUids, resolveRecord = resolveRecordUids, limit = LIMIT, lanes = LANES } = {}) {
  const parsed = (keys || []).map(parseKey).filter(Boolean);
  const uids = new Set(parsed.filter((p) => p.kind === "element").map((p) => p.uniqueId));
  const rows = parsed.filter((p) => p.kind !== "element");
  // Resolve a few at a time; the answers are folded in IN ORDER, so the row
  // named in a refusal is the same one whatever the network did.
  const answers = [];
  for (let i = 0; i < rows.length; i += lanes) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await Promise.all(rows.slice(i, i + lanes).map((p) => askRow(projectId, p, { prefixes, mode: "", resolve, resolveRecord })));
    answers.push(...batch);
  }
  // Counted from what the rows SAY they hold, not from what has been
  // folded in so far: a row's ids are only added once it is under the
  // ceiling, so counting the set would let the total slip past.
  let total = uids.size;
  for (let i = 0; i < rows.length; i += 1) {
    const p = rows[i];
    const r = answers[i];
    if (r && r.tooMany) return { uids: [], tooMany: { key: p, count: r.count, limit: r.limit } };
    // The TOTAL has the same ceiling as a row: rows that each pass can add
    // up to millions, and the tab dies without a word.
    total += (r && r.count) || 0;
    if (total > limit) return { uids: [], tooMany: { key: p, count: total, limit, total: true } };
    const byPrefix = (r && r.byPrefix) || {};
    for (const prefix of Object.keys(byPrefix)) {
      for (const id of String(byPrefix[prefix]).split(",")) if (id) uids.add(`${prefix}-${id}`);
    }
    for (const u of (r && r.other) || []) uids.add(u);
  }
  return { uids: [...uids], tooMany: null };
}

// What the toggle acts on: the ticks when there are any, else what is
// picked in the model.
// THE CHECKBOXES DEFINE THE SET; A CLICK IS ORDINARY SELECTION (owner,
// 2026-09-20). This used to fall back to the viewer's picks when nothing
// was ticked, so an inspection click silently BECAME the set: it moved
// the isolate boundary, and it joined the printed layout and the
// installed/not-installed schedule. The set is now the ticks alone -
// picks are a highlight inside it, and they have their own bulk bar.
export const sourceKeys = (checked) => (checked && checked.length ? checked : []);
